import { useRef, useState } from "react";
import { uploadFile, uploadBrochure } from "@/services/upload";

interface UploadInputProps {
  value: string;
  onChange: (url: string) => void;
  accept?: string;
  type?: "image" | "brochure";
  label?: string;
}

export default function UploadInput({
  value,
  onChange,
  accept,
  type = "image",
  label,
}: UploadInputProps) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const isImage =
    type === "image" &&
    (value ? /\.(jpe?g|png|webp|svg|gif|avif)$/i.test(value.split("?")[0]) : false);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res =
        type === "brochure"
          ? await uploadBrochure(file, "nomadlivings")
          : await uploadFile(file);
      onChange(res.url);
    } catch (err: any) {
      alert(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const defaultAccept = type === "brochure" ? "application/pdf" : "image/*,.svg";

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <div className="relative flex items-center justify-center w-16 h-16 rounded-lg border border-background-200 bg-background-100/70 overflow-hidden shrink-0">
          {isImage ? (
            <img src={value} alt="Uploaded" className="w-full h-full object-cover" />
          ) : (
            <i
              className={`text-2xl text-foreground-500 ${type === "brochure" ? "ri-file-pdf-2-line" : "ri-file-line"}`}
            ></i>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center w-16 h-16 rounded-lg border border-dashed border-background-300 bg-background-100/50 text-foreground-400 shrink-0">
          <i
            className={`text-2xl ${type === "brochure" ? "ri-file-pdf-line" : "ri-image-add-line"}`}
          ></i>
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-4 py-2 hover:bg-background-100 disabled:opacity-60"
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> Uploading...
            </>
          ) : (
            <>
              <i className="ri-upload-2-line"></i> {label || "Upload"}
            </>
          )}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="inline-flex items-center gap-1 text-xs text-foreground-600 hover:text-accent-700"
          >
            <i className="ri-delete-bin-line"></i> Remove
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={accept || defaultAccept}
        className="hidden"
        onChange={onFile}
      />
    </div>
  );
}